import moment from 'moment';
import momentTz from 'moment-timezone';

import { getTimezoneByZipCode } from './zipcodeToTimeZone';
import { CT_TIMEZONE_AREA } from '../constants';

import type { RangePickerProps } from 'antd/lib/date-picker';
import type { Duration, Moment } from 'moment';

export const disableNonFriday = (current: Moment) => {
  // Disable dates that are not Friday
  return current && current.day() !== 5;
};

export const disableNonSaturday = (current: Moment) => {
  // Disable dates that are not Saturday
  return current && current.day() !== 6;
};

export const disableAllDatesExceptFirstOfMonth = (current: Moment) => {
  // Check if the date is the first day of the month
  return current.date() !== 1;
};

export const disableAllDatesExceptLastOfMonth = (current: Moment) => {
  // Check if the date is the last day of the month
  const lastDayOfMonth = moment(current).endOf('month').date();
  return current.date() !== lastDayOfMonth;
};

export const disabledFutureDate: RangePickerProps['disabledDate'] = (current) => {
  // Can not select date and time after current
  return current && moment(current).isAfter(moment(Date.now()), 'day');
};

export function applyTimeToDate(date: Moment, time: Moment): Moment {
  return (
    date
      .clone()
      // Apply hour and minute from the selected time
      .hour(time.hour())
      .minute(time.minute())
      // Zero out sub-minute precision to keep a clean timestamp
      .second(0)
      .millisecond(0)
  );
}

export const disabledPastDates: RangePickerProps['disabledDate'] = (current) => {
  // Disable all past dates
  return current && moment(current).isBefore(moment(Date.now()), 'day');
};

const range = (start: number, end: number) => {
  const result = [];
  for (let i = start; i < end; i++) {
    result.push(i);
  }
  return result;
};

export const disabledHoursAndMinutesForToday = (timestamp: Moment) => {
  let disabledHours: any[] = [],
    disabledMinutes: any[] = [];

  const currentDateTime = moment(new Date());
  const calendarDateTime = moment(timestamp);
  if (calendarDateTime.isSame(currentDateTime, 'day')) {
    const pickedCurrentHour = calendarDateTime.hours();
    const currentHour = currentDateTime.hours();
    disabledHours = range(0, 24).splice(currentDateTime.hours() + 1, 24);

    if (pickedCurrentHour === currentHour) {
      disabledMinutes = range(0, 60).splice(currentDateTime.minutes() + 1, 60);
    }
  }

  return { disabledHours, disabledMinutes };
};

export const disabledFutureHoursAndMinutes = (timestamp: Moment) => {
  let disabledHours: any[] = [],
    disabledMinutes: any[] = [];

  const currentDateTime = moment(new Date());
  const calendarDateTime = moment(timestamp);
  if (calendarDateTime.isSame(currentDateTime, 'day')) {
    const pickedCurrentHour = calendarDateTime.hours();
    const currentHour = currentDateTime.hours();
    disabledHours = range(0, 24).splice(currentDateTime.hours() + 1, 24);

    if (pickedCurrentHour === currentHour) {
      disabledMinutes = range(0, 60).splice(currentDateTime.minutes() + 1, 60);
    }
  }

  return { disabledHours: () => disabledHours, disabledMinutes: () => disabledMinutes };
};

export const disabledTimeOutsideRange = (current: Moment | null, hoursFromNow: number) => {
  if (!current) return {};

  const now = moment();
  const allowedTimeFromNow = moment().subtract(hoursFromNow, 'hours');

  if (current.isSame(now, 'day')) {
    // For today's date, disable hours beyond the current hour and minutes beyond the current minute in the current hour
    return {
      disabledHours: () =>
        Array.from({ length: 24 }, (_, i) => i).filter((hour) => hour > now.hour()),
      disabledMinutes: (selectedHour: number) =>
        selectedHour === now.hour()
          ? Array.from({ length: 60 }, (_, i) => i).filter((minute) => minute > now.minute())
          : [],
    };
  } else if (current.isSame(allowedTimeFromNow, 'day')) {
    // For the boundary date 'hoursFromNow' hours ago,
    // disable hours before the boundary hour and minutes before the boundary minute in the boundary hour
    return {
      disabledHours: () =>
        Array.from({ length: 24 }, (_, i) => i).filter((hour) => hour < allowedTimeFromNow.hour()),
      disabledMinutes: (selectedHour: number) =>
        selectedHour === allowedTimeFromNow.hour()
          ? Array.from({ length: 60 }, (_, i) => i).filter(
              (minute) => minute < allowedTimeFromNow.minute(),
            )
          : [],
    };
  }

  // For dates within the range, allow all hours and minutes
  return {};
};

export const disabledDatesOutsideRange = (
  current: Moment,
  startDateFromTodayInDays: number, // should be negative if start date is before today
  endDateFromTodayInDays: number,
) => {
  const today = moment(new Date());
  const startDateCutoff = today.clone().add(startDateFromTodayInDays, 'days').startOf('day');
  const endDateCutoff = today.clone().add(endDateFromTodayInDays, 'days').startOf('day');
  // Cannot select date and time before startDateCutoff and after endDateCutoff
  return (
    current && (current.isBefore(startDateCutoff, 'day') || current.isAfter(endDateCutoff, 'day'))
  );
};

// convert seconds to hh:mm
export const getHHMMfromSeconds = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  let minutes: number | string = Math.floor((seconds % 3600) / 60);
  minutes = minutes < 10 ? `0${minutes}` : minutes;
  return `${hours}:${minutes}`;
};

// validate end_date greater than start_date
export const validateStartDateLessOrEqualEndDate = (startDate: Moment, endDate: Moment) => {
  if (startDate.isSameOrBefore(endDate)) {
    return true;
  }
  return false;
};

// This function returns time duration in (hh mm) format, based on difference between a end date and start date
export const calculateDateDifferenceInHHMM = (startDate: Moment, endDate: Moment) => {
  const durationObject = moment.duration(endDate.diff(startDate));
  return durationToHHMM(durationObject);
};

export const formatSecondsToHHMMRounded = (duration_in_sec: number) => {
  const rem = duration_in_sec % 60;
  const val = rem !== 0 ? duration_in_sec + (60 - rem) : duration_in_sec;
  return durationToHHMM(moment.duration(val, 'seconds'));
};

// This function returns time duration in (hh mm) format based on number of seconds
export const formatSecondsToHHMM = (duration_in_sec: number) => {
  return durationToHHMM(moment.duration(duration_in_sec, 'seconds'));
};

export const secondsToHoursWithDecimalDigits = (seconds: number) => {
  if (!seconds || seconds < 0) {
    return 0;
  }
  return seconds / 3600;
};

export const formatSecondsToHHMM2 = (duration_in_sec: number) => {
  return durationToHHMM2(moment.duration(duration_in_sec, 'seconds'));
};

// This function returns time duration in (hh mm) format based on number of seconds
export const formatSecondsToHHMM3 = (duration_in_sec: number) => {
  return durationToHHMM3(moment.duration(duration_in_sec, 'seconds'));
};

export const durationToHHMM2 = (durationObject: Duration) => {
  const minutesString =
    Math.floor(durationObject.minutes()) === 0
      ? '0'
      : ('0' + Math.floor(durationObject.minutes())).slice(-2);
  const hoursString = `${Math.floor(durationObject.asHours())}`;
  return `${hoursString} h ${minutesString} m`;
};

export const durationToHHMM = (durationObject: Duration) => {
  // For minuteString, we display 0 as 0, but we display 5 as 05
  const minutesString =
    Math.floor(durationObject.minutes()) === 0
      ? '0'
      : ('0' + Math.floor(durationObject.minutes())).slice(-2);
  const hoursString = '' + Math.floor(durationObject.asHours());
  return hoursString === '0'
    ? minutesString === '0'
      ? `${minutesString} m`
      : ` ${minutesString} m`
    : minutesString === '0'
      ? `${hoursString} h`
      : `${hoursString} h ${minutesString} m`;
};

export const durationToHHMM3 = (durationObject: Duration) => {
  // For minuteString, we display 0 as 0, but we display 5 as 05
  const minutesString =
    Math.floor(durationObject.minutes()) === 0
      ? '0'
      : ('0' + Math.floor(durationObject.minutes())).slice(-2);
  const hoursString = '' + Math.floor(durationObject.asHours());
  return hoursString === '0'
    ? minutesString === '0'
      ? `${minutesString}m`
      : ` ${minutesString}m`
    : minutesString === '0'
      ? `${hoursString}h`
      : `${hoursString}h ${minutesString}m`;
};

export const toUtcOffsetDisplay = (duration: number) => {
  if (duration === 0) return '';

  const time = moment.duration(duration, 'seconds');
  return `${duration > 0 ? '+' : ''}${time.hours()}:${time.minutes()}`;
};

export const isWithinDateHourRange = (date: Moment, startHour: number, endHour: number) => {
  const startOfDay = moment(date).startOf('day').hours(startHour);
  const endOfDay = moment(date).startOf('day').hours(endHour);
  return date.isBetween(startOfDay, endOfDay) || date.isSame(startOfDay);
};

export const getDurationFromSeconds = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours === 0) {
    return `${minutes} mins`;
  }
  return `${hours}h ${minutes}m`;
};

export const dateFormatConverter = (val: Moment | string, source: string, target: string) =>
  moment(val, source).format(target);

export const getCurrentCTDateTime = () =>
  momentTz(momentTz().format('YYYY-MM-DD HH:mm:ss')).tz('America/Chicago');

export const convertDateTimeToCTTime = (dateTime: string) =>
  momentTz(moment(dateTime).format('YYYY-MM-DD HH:mm:ss')).tz('America/Chicago');

export const getUSTimeByZipcode = (
  datetime: Moment,
  zipCode: string | number | null | undefined,
): [Moment, string, boolean] => {
  let isTZIdentified = true;
  let zone = CT_TIMEZONE_AREA;
  const normalizedZip = zipCode === null || zipCode === undefined ? null : String(zipCode);
  const identifiedTz = getTimezoneByZipCode(normalizedZip);
  if (identifiedTz) {
    zone = identifiedTz;
  } else {
    isTZIdentified = false;
  }

  return [momentTz(datetime).tz(zone), momentTz().tz(zone).format('z'), isTZIdentified];
};

export const getEndDateFromStartDateAndDuration = (
  startDate: Moment | string,
  duration: number,
  unit: 'seconds' | 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years',
) => moment(startDate).add(duration, unit);

export const combineMomentDateTime = (dateMoment: Moment, timeMoment: Moment) => {
  return moment({
    year: dateMoment.year(),
    month: dateMoment.month(),
    day: dateMoment.date(),
    hour: timeMoment.hour(),
    minute: timeMoment.minute(),
    second: 0,
    millisecond: 0,
  });
};

export function generateWeekRanges(startDate: Moment, endDate: Moment, period: 'week' | 'month') {
  const start = moment(startDate).startOf(period);
  const end = moment(endDate).endOf(period);
  const weeks = [];

  const current = start.clone();

  while (current.isBefore(end)) {
    const weekStart = current.clone();
    const weekEnd = current.clone().endOf(period);

    weeks.push({
      startDate: weekStart.format('YYYY-MM-DD'),
      endDate: weekEnd.format('YYYY-MM-DD'),
    });

    current.add(1, period);
  }

  return weeks.reverse();
}

// Calculates difference between two times in seconds with rounding to nearest 15 minutes.
// - If remainder to next 15-min block is < 8 minutes, round down
// - If remainder is ≥ 8 minutes, round up
// - Do not round up if total duration is less than 30 minutes
export const getRoundedDiffInSecondsToQuarterHour = (startDate: Moment, endDate: Moment) => {
  if (!startDate || !endDate) return 0;

  const rawSeconds = Math.max(0, endDate.diff(startDate, 'seconds'));
  const quarterSeconds = 15 * 60; // 900
  const remainder = rawSeconds % quarterSeconds;

  // Always round down if less than 8 minutes remainder or total < 30 minutes
  const eightMinutes = 8 * 60; // 480
  const thirtyMinutes = 30 * 60; // 1800

  if (remainder === 0) return rawSeconds;

  const roundDown = rawSeconds - remainder;

  if (remainder < eightMinutes || rawSeconds < thirtyMinutes) {
    return roundDown;
  }

  // Round up otherwise
  return roundDown + quarterSeconds;
};
